/**
 * This file contains some workarounds to ensure that the editor is rendered
 * the way Whiteboards expects it to be rendered.
 *
 * Long term the editor wants to decouple the Appearance (ie. comment, chromeless etc.)
 * from the editor so that products can use whatever styling they want or use
 * a configured Editor Appearance which would simplify these.
 * https://product-fabric.atlassian.net/wiki/spaces/EA/pages/3654221898/Medium+Term+Plans
 */
:global .assistive {
  border: 0 !important;
  clip: rect(1px, 1px, 1px, 1px) !important;
  height: 1px !important;
  overflow: hidden !important;
  padding: 0 !important;
  position: absolute !important;
  width: 1px !important;
  white-space: nowrap !important;
}

.EditorContainer .Editor :global .ProseMirror h1 {
  font-size: calc(var(--canvas-h1-font-size) * var(--canvas-editor-font-scale));
  line-height: calc(var(--canvas-h1-line-height) * var(--canvas-editor-font-scale));
  margin-top: calc(var(--canvas-h1-margin-top) * var(--canvas-editor-font-scale));
  font-weight: var(--canvas-h1-font-weight);
  color: var(--canvas-editor-color);
  letter-spacing: var(--canvas-default-kerning);
}

.EditorContainer .Editor :global .ProseMirror h2 {
  font-size: calc(var(--canvas-h2-font-size) * var(--canvas-editor-font-scale));
  line-height: calc(var(--canvas-h2-line-height) * var(--canvas-editor-font-scale));
  margin-top: calc(var(--canvas-h2-margin-top) * var(--canvas-editor-font-scale));
  font-weight: var(--canvas-h2-font-weight);
  color: var(--canvas-editor-color);
  letter-spacing: var(--canvas-default-kerning);
}

.EditorContainer .Editor :global .ProseMirror h3 {
  font-size: calc(var(--canvas-h3-font-size) * var(--canvas-editor-font-scale));
  line-height: calc(var(--canvas-h3-line-height) * var(--canvas-editor-font-scale));
  margin-top: calc(var(--canvas-h3-margin-top) * var(--canvas-editor-font-scale));
  font-weight: var(--canvas-h3-font-weight);
  color: var(--canvas-editor-color);
  letter-spacing: var(--canvas-default-kerning);
}

.EditorContainer .Editor :global .ProseMirror h4 {
  font-size: calc(var(--canvas-h4-font-size) * var(--canvas-editor-font-scale));
  line-height: calc(var(--canvas-h4-line-height) * var(--canvas-editor-font-scale));
  margin-top: calc(var(--canvas-h4-margin-top) * var(--canvas-editor-font-scale));
  font-weight: var(--canvas-h4-font-weight);
  color: var(--canvas-editor-color);
  letter-spacing: var(--canvas-default-kerning);
}

.EditorContainer .Editor :global .ProseMirror h5 {
  font-size: calc(var(--canvas-h5-font-size) * var(--canvas-editor-font-scale));
  line-height: calc(var(--canvas-h5-line-height) * var(--canvas-editor-font-scale));
  margin-top: calc(var(--canvas-h5-margin-top) * var(--canvas-editor-font-scale));
  font-weight: var(--canvas-h5-font-weight);
  color: var(--canvas-editor-color);
  letter-spacing: var(--canvas-default-kerning);
}

.EditorContainer .Editor :global .ProseMirror h6 {
  font-size: calc(var(--canvas-h6-font-size) * var(--canvas-editor-font-scale));
  line-height: calc(var(--canvas-h6-line-height) * var(--canvas-editor-font-scale));
  margin-top: calc(var(--canvas-h6-margin-top) * var(--canvas-editor-font-scale));
  font-weight: var(--canvas-h6-font-weight);
  text-transform: none;
  color: var(--canvas-editor-color);
  letter-spacing: var(--canvas-default-kerning);
}

.EditorContainer .Editor :global .ProseMirror .emoji-common-emoji-sprite {
  width: calc(20px * var(--canvas-editor-font-scale)) !important;
  height: calc(20px * var(--canvas-editor-font-scale)) !important;
  min-width: calc(20px * var(--canvas-editor-font-scale)) !important;
  min-height: calc(20px * var(--canvas-editor-font-scale)) !important;
}

/* Keep the line height normal for emoji so that it matches the webgl */
.EditorContainer .Editor :global .ProseMirror .emojiView-content-wrap {
  line-height: normal;
}

.CommentRenderer {
  user-select: text;
}

.CommentRenderer * {
  user-select: auto;
}

.CommentRenderer,
.EditorContainer {
  width: 100%;
}

.CommentRenderer,
.EditorContainer .EditorContainer {
  height: 100%;
}

.Editor.editorLoading ::selection {
  background-color: transparent;
}

:global .ProseMirror,
:global .ak-renderer-wrapper .ak-renderer-document {
  /*
    Ensures casing is consistent between Harfbuzz and the DOM. @see
    {https://harfbuzz.github.io/shaping-opentype-features.html}

    This must match the features provided to Harfbuzz: @see
    {file://./../../text/src/shaper/shaper.ts}
  */
  font-feature-settings:
    'abvm', 'blwm', 'ccmp', 'locl', 'mark', 'mkmk', 'rlig', 'calt', 'clig', 'curs',
    'dist', 'kern', 'liga', 'rclt';
  font-optical-sizing: var(--canvas-editor-font-optical-sizing, none);
}

.fontShapingRoot {
  /*
    Ensures casing is consistent between Harfbuzz and the DOM. @see
    {https://harfbuzz.github.io/shaping-opentype-features.html}

    This must match the features provided to Harfbuzz: @see
    {file://./../../text/src/shaper/shaper.ts}
  */
  font-feature-settings:
    'abvm', 'blwm', 'ccmp', 'locl', 'mark', 'mkmk', 'rlig', 'calt', 'clig', 'curs',
    'dist', 'kern', 'liga', 'rclt';
}

.Editor :global .ProseMirror {
  min-height: var(--canvas-editor-min-height);
  box-sizing: border-box;
  overflow: visible;
  display: flex !important;
  flex-direction: column;
  justify-content: var(--canvas-editor-vertical-alignment);
  top: 0;
  left: 0;
  right: 0;
  color: var(--canvas-editor-color);
  text-align: var(--canvas-editor-alignment);
  text-rendering: geometricprecision;

  @-moz-document url-prefix("") {
    -moz-osx-font-smoothing: grayscale;
  }

  /*
    NB: ProseMirror requires this setting. This means trailing space is always
    rendered, even in the case of centred text (leading to semi-inaccurate alignment).
    @see {https://github.com/ProseMirror/prosemirror/issues/981#issuecomment-538098050}
   */
  white-space: var(--canvas-editor-content-wrapping) !important;
  overflow-wrap: break-word;

  /*
    NB: we apply some high-level character-spacing adjustments to all rendered text,
    keeping in mind that we are using `Inter` as our font of choice.
  */
  letter-spacing: var(--canvas-default-kerning);
}

.EditorContainer .Editor :global .ProseMirror {
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale)) !important;
}

.Editor {
  width: 100%;
}

.Editor.flexibleHeight {
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 100%;
}

.Editor.flexibleWidth {
  display: block;
  width: 100%;
  max-width: none;
  min-height: unset;
  min-width: 18px;
}

.Editor.fixedWidth :global .ProseMirror {
  width: var(--canvas-editor-width);
}

/**
 * This is a stopgap to ensure that font feature settings aren't overriden by the new
 tokens introduced in `platform_editor_typography_ugc`, until we align on intended
 behaviour with the new typography approach.

 TODO: https://product-fabric.atlassian.net/browse/CNS-18515
 https://hello.atlassian.net/wiki/spaces/CAN/pages/5560015079/Mentions+-+Alignment+with+Editor
 Alignment TBC https://atlassian.slack.com/archives/C084ZD1TBE3/p1752213031777559

 * It can be tested locally by setting `font-feature-settings` to `initial` to simulate
 the shorthand token e.g. rendered out as `font: 400 1em / 1.714 Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif`
   in dropping when the shorthand https://developer.mozilla.org/en-US/docs/Web/CSS/font is
   used.

   The _current_ approach that canvas has(had), results in better rendered text where
   the appearance is smoothed out with the various font-feature-settings we leverage
   inside
   of the font, e.g. a more aligned `@` when next to capitals, or --> turning into → where
   this is the most glaring difference in our editor/rendered text behaviour.

   https://product-fabric.atlassian.net/browse/CNS-18349
   https://product-fabric.atlassian.net/browse/CNS-18506
 */
.EditorContainer .Editor :global .ProseMirror p,
.EditorContainer .Editor :global .ProseMirror h1,
.EditorContainer .Editor :global .ProseMirror h2,
.EditorContainer .Editor :global .ProseMirror h3,
.EditorContainer .Editor :global .ProseMirror h4,
.EditorContainer .Editor :global .ProseMirror h5,
.EditorContainer .Editor :global .ProseMirror h6 {
  font-family: inherit;
  font-feature-settings: inherit;
  font-optical-sizing: inherit;
}

.EditorContainer .Editor :global .ProseMirror p {
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
  line-height: calc(var(--canvas-p-line-height) * var(--canvas-editor-font-scale));
  margin-top: calc(var(--canvas-p-margin-top) * var(--canvas-editor-font-scale));
  font-weight: var(--canvas-p-font-weight);
  color: var(--canvas-editor-color);
  letter-spacing: var(--canvas-default-kerning);
}

/*
  These CSS entries have specific values to get list padding the way we want it for stickies
  @see: {file://./../../../docs/design/sticky-bullet-list-dimensions.png}
*/
.EditorContainer .Editor :global .ProseMirror ol {
  margin: 0;
  padding: 0;
  margin-top: calc(var(--canvas-ol-margin-top) * var(--canvas-editor-font-scale));
  text-align: left;
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
}

.EditorContainer .Editor :global .ProseMirror ul {
  margin: 0;
  padding: 0;
  margin-top: calc(var(--canvas-ul-margin-top) * var(--canvas-editor-font-scale));
  text-align: left;
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
}

.EditorContainer .Editor :global .ProseMirror ul > li {
  margin: 0;
  margin-left: calc(
    var(--canvas-ul-padding-left) * var(--canvas-editor-font-scale)
  ) !important;
  padding-left: calc(
    var(--canvas-ul-li-padding-left) * var(--canvas-editor-font-scale)
  ) !important;
  text-align: left;
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
}

.EditorContainer .Editor :global .ProseMirror ol > li {
  margin: 0;
  margin-left: calc(
    var(--canvas-ol-padding-left) * var(--canvas-editor-font-scale)
  ) !important;
  padding-left: calc(
    var(--canvas-ol-li-padding-left) * var(--canvas-editor-font-scale)
  ) !important;
  text-align: left;
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
}

.EditorContainer .Editor :global .ProseMirror p:only-child {
  min-height: 1em;
}

.EditorContainer .Editor :global .editor-root .ProseMirror {
  position: fixed;
  top: 0;
  left: 0;
  transform: translate3d(0, -100%, 0);
  transform-origin: center center;
  pointer-events: initial;
}

.EditorContainer .Editor :global .ProseMirror:focus {
  outline: none;
}

.Editor :global .ProseMirror.not-interactive {
  pointer-events: none;
}

/* Override for letter spacing set by Editor */
/* stylelint-disable-next-line */
.EditorContainer .Editor :global .ak-editor-content-area .ProseMirror p {
  letter-spacing: var(--canvas-default-kerning);
}

.Editor :global .ProseMirror ol[data-indent-level='1'],
.Editor :global .ProseMirror ol[data-indent-level='4'],
.Editor :global .ProseMirror ol[data-indent-level='7'] {
  list-style-type: decimal;
}

.Editor :global .ProseMirror ol[data-indent-level='2'],
.Editor :global .ProseMirror ol[data-indent-level='5'],
.Editor :global .ProseMirror ol[data-indent-level='8'] {
  list-style-type: lower-alpha;
}

.Editor :global .ProseMirror ol[data-indent-level='3'],
.Editor :global .ProseMirror ol[data-indent-level='6'],
.Editor :global .ProseMirror ol[data-indent-level='9'] {
  list-style-type: lower-roman;
}

.Editor :global .ProseMirror ol ::marker {
  /* allows the numbers to be spaced consistently with our webGL rendering */
  font-variant-numeric: normal;
}

.Editor :global .ProseMirror ul[data-indent-level='1'] > li::marker,
.Editor :global .ProseMirror ul[data-indent-level='4'] > li::marker,
.Editor :global .ProseMirror ul[data-indent-level='7'] > li::marker {
  font-family: inherit;
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
}

.Editor :global .ProseMirror ul[data-indent-level='2'] > li::marker,
.Editor :global .ProseMirror ul[data-indent-level='5'] > li::marker,
.Editor :global .ProseMirror ul[data-indent-level='8'] > li::marker {
  font-family: inherit;
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
}

.Editor :global .ProseMirror ul[data-indent-level='3'] > li::marker,
.Editor :global .ProseMirror ul[data-indent-level='6'] > li::marker,
.Editor :global .ProseMirror ul[data-indent-level='9'] > li::marker {
  font-family: inherit;
  font-size: calc(var(--canvas-p-font-size) * var(--canvas-editor-font-scale));
}

.Editor :global .ProseMirror ul[data-indent-level='1'] > li,
.Editor :global .ProseMirror ul[data-indent-level='4'] > li,
.Editor :global .ProseMirror ul[data-indent-level='7'] > li {
  list-style-type: '\2022 ';
}

.Editor :global .ProseMirror ul[data-indent-level='2'] > li,
.Editor :global .ProseMirror ul[data-indent-level='5'] > li,
.Editor :global .ProseMirror ul[data-indent-level='8'] > li {
  list-style-type: '\25e6 ';
}

.Editor :global .ProseMirror ul[data-indent-level='3'] > li,
.Editor :global .ProseMirror ul[data-indent-level='6'] > li,
.Editor :global .ProseMirror ul[data-indent-level='9'] > li {
  list-style-type: '\25aa\FE0E ';
}

.EditorContainer .Editor :global .ProseMirror ul > li + li {
  margin-top: calc(var(--canvas-ul-li-margin-top) * var(--canvas-editor-font-scale));
}

.EditorContainer .Editor :global .ProseMirror ol > li + li {
  margin-top: calc(var(--canvas-ol-li-margin-top) * var(--canvas-editor-font-scale));
}

/* End list-padding fixes */

/* Chromeless editor for new `ComposableEditor` has padding on the bottom we want to fix */
.Editor :global div > .ProseMirror > :last-child {
  padding-bottom: 0 !important;
}

/* Fixes list items getting cut off, and scrollbars appearing when they shouldn't */
.Editor :global div {
  overflow-x: visible;
  overflow-y: visible;
}

.Editor :global .ProseMirror.ProseMirror-focused p:hover,
.Editor :global .ProseMirror.ProseMirror-focused h1:hover,
.Editor :global .ProseMirror.ProseMirror-focused h2:hover,
.Editor :global .ProseMirror.ProseMirror-focused h3:hover,
.Editor :global .ProseMirror.ProseMirror-focused h4:hover,
.Editor :global .ProseMirror.ProseMirror-focused h5:hover,
.Editor :global .ProseMirror.ProseMirror-focused h6:hover {
  cursor: text;
}

/* For paragraphs followed by headings */
.EditorContainer .Editor :global(.ProseMirror h1 + p),
.EditorContainer .Editor :global(.ProseMirror h2 + p),
.EditorContainer .Editor :global(.ProseMirror h3 + p),
.EditorContainer .Editor :global(.ProseMirror h4 + p),
.EditorContainer .Editor :global(.ProseMirror h5 + p),
.EditorContainer .Editor :global(.ProseMirror h6 + p) {
  margin-top: calc(
    var(--canvas-p-under-h-margin-top) * var(--canvas-editor-font-scale)
  ) !important;
}

/* For the first line of content */
.EditorContainer .EditorContainer .Editor :global(.ProseMirror *:first-child) {
  margin-top: 0;
}

/* Placeholder styles */

.Editor :global .ProseMirror .placeholder-decoration {
  color: rgb(var(--theme-n200));
  pointer-events: none;
  white-space: nowrap;
  user-select: none;
}

.Editor :global .ProseMirror .placeholder-decoration > span {
  pointer-events: none;
}

/* Placeholder wrapping if enabled */
.Editor.allowPlaceholderWrapping :global .ProseMirror .placeholder-decoration {
  white-space: unset;
}

.Editor :global .ProseMirror .mentionView-content-wrap span {
  /*
    NB: without this rule, mentions specify their own line heights. This leads to
    uneven rendering of lines of text, especially with mentions in them.
  */
  line-height: inherit;
}

/* Overrides the editor's mention pill border-radius to smooth it out at scale.
This is a hack and needs a proper solution from an Editor patch as per https://atlassian.slack.com/archives/CFG3PSQ9E/p1750654170141449?thread_ts=1750644300.115089&cid=CFG3PSQ9E */
.Editor :global .ProseMirror .editor-mention-primitive {
  border-radius: calc(0.8em * var(--canvas-editor-font-scale));
}

/* Overrides for mention wrapping behavior to match GL rendering.
 *
 * After bumping @atlassian/smart-mention-resource to 3.1.0, Atlaskit's
 * .inlineNodeView > :not(.zeroWidthSpaceContainer) rule applies white-space: pre-wrap,
 * which caused mentions to break character-by-character in editing mode.
 *
 * We scope fixes by editor width mode:
 * - fixedWidth editors (stickies, shapes): Allow mentions to wrap at spaces when constrained,
 *   falling back to character breaks in very narrow widths, matching GL's break-mention strategy.
 * - flexibleWidth editors (free text): Keep mentions on a single line to match GL rendering
 *   where free text mentions don't wrap.
 */
.Editor.fixedWidth
  :global
  .ProseMirror
  .inlineNodeView.mentionView-content-wrap
  > .editor-mention-primitive {
  overflow-wrap: anywhere;
  white-space: pre-wrap !important;
  word-break: normal;
}

/* Prevent trailing zero-width helper spans from creating extra vertical space
 * in fixed-width editors. These helpers exist for caret positioning but shouldn't
 * contribute to layout when the mention wraps across lines.
 */
.Editor.fixedWidth
  :global
  .ProseMirror
  .inlineNodeView.mentionView-content-wrap
  > .inlineNodeViewAddZeroWidthSpace {
  overflow-wrap: normal;
  white-space: nowrap !important;
  word-break: normal;
}

.Editor.flexibleWidth
  :global
  .ProseMirror
  .inlineNodeView.mentionView-content-wrap
  > .editor-mention-primitive {
  white-space: nowrap !important;
  word-break: normal;
}

.Editor.allowPlaceholderWrapping :global .ProseMirror .placeholder-decoration > span {
  white-space: unset;
}

.CommentEditor {
  border: var(--ds-border-width, 1px) solid;
  color: var(--ds-border-focused, #4c9aff);
  border-radius: var(--ds-radius-small, 3px);
  box-sizing: border-box;
  min-height: 64px;

  /* 1-overflow: Needed to support typeahead styling - it shows up as part of
  the Editor, which means that z-indexing needs to be respected - editor should
  display above buttons in the comment panel */
  overflow: visible;
  position: relative;
}

.CommentEditor.ConsolidatedComments {
  border: var(--ds-border-width-selected, 2px) solid var(--ds-border-brand, #0c66e4) !important;
}

.CommentEditor div[data-editor-popup='true'] {
  /* 2-overflow: We required fixed styling to ensure the typeahead element is
  correctly displayed in the correct position on screen. The z-indexing &
  layering is achieved above via (1-overflow) */
  position: fixed !important;
}

.CommentEditor :global .ak-editor-content-area {
  color: var(--ds-text, #172b4d);
  padding: 8px;
}

.CommentEditorButtons {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
}

.CommentEditorButtons .iconButtonLeft {
  margin-right: auto;
}

.CommentTyping {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  min-height: 12px;
  color: var(--ds-text-subtle, #44546f);
}

.NewCommentTextBox {
  border: var(--ds-border-width, 1px) solid var(--ds-border, #091e4224);
  color: var(--ds-text-subtlest, #626f86);
  border-radius: var(--ds-radius-small, 3px);
  padding: 8px;

  /* When the editor is focused it becomes a text area. Upon this happening,
  the text shifts upwards by a very little amount, leading to some perceivable,
  sub-pixel jank. This rule helps us remove that. */
  padding-top: 7.5px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;

  /* Only using important because they do... */
  line-height: 1.714em !important;
  gap: 10px;
}

.NewCommentTextBox.ConsolidatedComments {
  height: 32px;
  border: var(--ds-border-width, 1px) solid
    var(--ds-border-input, var(--ds-background-accent-gray-subtler, #dcdfe4));
  padding: 4px !important;
}
